import type { Job, NurseFullRole, RoleJobRecommendation } from "../../../../utils/models";

export type ResolvedRecommendation = {
	/** The recommended jobs, in backend order, restricted to what the picker actually offers. */
	jobs: Job[];
	/** True once every surviving recommendation is selected. Extra jobs beyond it are fine. */
	isSatisfied: boolean;
};

const NOTHING_RECOMMENDED: ResolvedRecommendation = { jobs: [], isSatisfied: true };

/**
 * Resolve the backend's recommendation for `role` against the picker's own option list (DAT-586).
 *
 * Ids the department doesn't offer are dropped: the hint must never name a job the admin has no
 * checkbox for. This is a real case, not defensive padding — `nurse.job_ids` is nurse-level, so an
 * employee can carry a job from a sibling department that this one never enabled, and the backend's
 * catalog view can lag the department config the form is rendering against.
 *
 * `isSatisfied` treats the recommendation as a floor rather than an exact match: selecting a
 * superset counts as done, which is what lets the hint disappear once the admin has acted on it.
 */
export const resolveRecommendedJobs = (
	recommendations: RoleJobRecommendation[] | undefined,
	role: NurseFullRole | undefined,
	availableJobs: Job[],
	selectedJobIds: string[],
): ResolvedRecommendation => {
	if (!recommendations?.length || !role) return NOTHING_RECOMMENDED;
	const recommendedJobIds = recommendations.find((r) => r.role === role)?.jobIds;
	if (!recommendedJobIds?.length) return NOTHING_RECOMMENDED;

	const jobById = new Map(availableJobs.map((job) => [job.id, job]));
	const jobs = recommendedJobIds.map((id) => jobById.get(id)).filter((job): job is Job => !!job);
	return { jobs, isSatisfied: jobs.every((job) => selectedJobIds.includes(job.id)) };
};

/**
 * The selection after applying the recommendation: the current picks, plus any recommended job not
 * already chosen (DAT-586).
 *
 * A merge rather than a replace, for two reasons. The admin may have deliberately added a job the
 * roster doesn't predict, and applying a suggestion must never silently discard it. And existing
 * order is preserved, so `jobIds[0]` — the primary job, which drives shift assignment — is left
 * where it is; only when nothing is selected yet does the recommendation's first job become primary.
 */
export const applyRecommendedJobs = (selectedJobIds: string[], recommendedJobs: Job[]): string[] => [
	...selectedJobIds,
	...recommendedJobs.map((job) => job.id).filter((id) => !selectedJobIds.includes(id)),
];
